import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Minus, Globe } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';
import usePriceStream from '@/hooks/usePriceStream';
import { ccToFlag } from '@/lib/flagEmoji';
import { unusualVolumeBadge } from '@/lib/volume';
import { useActivePortfolioId } from '@/components/PortfolioSelector';

// Oct 2026 — the per-position withholding row reads
// `/api/stocks/:t/adr-withholding` (same source as the /foreign-dividend-stocks
// hub and the Insights ADR card) instead of a hand-maintained rate table.
function useAdrWithholding(ticker, isAdr) {
  const [adr, setAdr] = useState(null);
  useEffect(() => {
    if (!ticker || !isAdr) return undefined;
    let cancelled = false;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/stocks/${encodeURIComponent(ticker)}/adr-withholding`)
      .then((r) => { if (!cancelled) setAdr(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ticker, isAdr]);
  return adr;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MyPositionTab — May 2026.
 *
 * Renders inside the Stock Modal when the user owns the stock AND
 * the stock is a dividend payer. The tab trigger itself is
 * conditionally rendered by StockModal (see ownsThisStock +
 * isDividendStock guards), so this body only ever mounts for
 * eligible owners.
 *
 * Round 92o++++++++N++++++++ — SEC filings (10-K / 8-K) used to
 * live inside this tab. They moved to the dedicated
 * `TenK8KRisksTab` component so non-owners and watchers can also
 * read filings + risk factors.
 *
 * Data sources:
 *   • /api/portfolio/{ticker}/my-position — single aggregated read
 *     covering position roll-up, daily OHLC, YoC, dividend-growth-
 *     since-first-buy, sector, and % of portfolio income.
 *   • usePriceStream([ticker])         — same SSE the other tabs use.
 *     We OVERLAY the live price on top of the aggregated payload and
 *     recompute Market Value / Day P/L / Unrealized P/L on every tick.
 */
function tone(n, { invert = false } = {}) {
  if (n === null || n === undefined || Number.isNaN(n) || n === 0) {
    return 'text-muted-foreground';
  }
  const positive = invert ? n < 0 : n > 0;
  return positive
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-rose-600 dark:text-rose-400';
}

function fmtMoney(n, { sign = false, fractional = false } = {}) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const opts = fractional
    ? { minimumFractionDigits: 4, maximumFractionDigits: 4 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  const abs = Math.abs(n).toLocaleString('en-US', opts);
  if (sign && n > 0) return `+$${abs}`;
  if (n < 0) return `-$${abs}`;
  return `$${abs}`;
}

function fmtPct(n, { sign = true } = {}) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const v = n.toFixed(2);
  if (sign && n > 0) return `+${v}%`;
  return `${v}%`;
}

// Compact volume formatter — 22,678 → "22.7K", 1,400,000 → "1.40M".
function fmtVolume(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
}


function fmtShares(n) {
  if (n === null || n === undefined) return '—';
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(4).replace(/\.?0+$/, '');
}

// Compact 4-up stat card used across the top OHLC strip and the
// dividend stats block. `tone` flips colour for positive/negative.
function Stat({ label, value, suffix = '', valueTone = '', testId, hint, caption = null }) {
  return (
    <div
      className="rounded-md border bg-card/60 px-2.5 py-1.5 min-w-0"
      data-testid={testId}
      title={hint || undefined}
    >
      <p className="text-[9px] uppercase tracking-wide text-muted-foreground font-bold truncate">
        {label}
      </p>
      <p className={`text-sm font-bold font-mono truncate ${valueTone}`}>
        {value}{suffix}
      </p>
      {caption && (
        <p className="text-[9px] font-bold text-muted-foreground/80 truncate" data-testid={testId ? `${testId}-caption` : undefined}>
          {caption}
        </p>
      )}
    </div>
  );
}

// Today's date in US Eastern (the market timezone). Trims to YYYY-MM-DD
// for direct comparison against backend's `daily_date`.
function todayInET() {
  // toLocaleDateString with the en-CA locale gives us YYYY-MM-DD format.
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

// "as of Fri Jun 12" — short, friendly caption for prior-session prints.
function formatPriorSessionCaption(daily_date) {
  if (!daily_date) return null;
  try {
    // daily_date is YYYY-MM-DD. Construct at noon ET to dodge TZ rollovers.
    const d = new Date(`${daily_date}T12:00:00-05:00`);
    if (Number.isNaN(d.getTime())) return null;
    const wkday = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'America/New_York' });
    const mon = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'America/New_York' });
    const day = d.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'America/New_York' });
    return `as of ${wkday} ${mon} ${day}`;
  } catch {
    return null;
  }
}

function PositionXirrTile({ ticker }) {
  const { activeId } = useActivePortfolioId();
  const [x, setX] = useState(null);
  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    axios.get(`${API}/portfolio/${ticker}/xirr`, { params: activeId ? { portfolio_id: activeId } : {}, withCredentials: true })
      .then((r) => { if (!cancelled) setX(r.data); })
      .catch(() => { if (!cancelled) setX(null); });
    return () => { cancelled = true; };
  }, [ticker, activeId]);
  if (!x) return null;
  const has = x.xirr_pct !== null && x.xirr_pct !== undefined;
  const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return (
    <div className={`mt-2 rounded-md border bg-card/60 px-2.5 py-1.5 ${has ? '' : 'opacity-70'}`} data-testid="my-position-xirr">
      <p className="text-[9px] uppercase tracking-wide text-muted-foreground font-bold">
        Your annualised return on this holding (<AbbrTip term="XIRR">XIRR</AbbrTip>)
      </p>
      {has ? (
        <p className={`text-sm font-bold font-mono ${x.xirr_pct > 0 ? 'text-emerald-600 dark:text-emerald-400' : x.xirr_pct < 0 ? 'text-rose-600 dark:text-rose-400' : ''}`} data-testid="my-position-xirr-value">
          {x.xirr_pct > 0 ? '+' : ''}{Number(x.xirr_pct).toFixed(1)}% / yr
          <span className="ml-1.5 text-[10px] text-muted-foreground font-normal">
            over {x.years_elapsed} yrs · {fmt$(x.invested)} in → {fmt$(x.final_value)} now
            {x.cash_dividends > 0 ? ` + ${fmt$(x.cash_dividends)} cash dividends` : ''}
            {x.reinvested_dividends > 0 ? ` · ${fmt$(x.reinvested_dividends)} reinvested (DRIP)` : ''}
          </span>
        </p>
      ) : (
        <p className="text-sm font-bold font-mono text-muted-foreground" data-testid="my-position-xirr-skip">
          — <span className="text-[10px] font-normal">{x.skip_reason}</span>
        </p>
      )}
    </div>
  );
}

export default function MyPositionTab({ ticker, stock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { activeId } = useActivePortfolioId();

  // SSE for live price ticks on this single ticker
  const tickerList = useMemo(() => (ticker ? [ticker.toUpperCase()] : []), [ticker]);
  const { prices: livePrices } = usePriceStream(tickerList);
  const adr = useAdrWithholding(ticker, !!stock?.is_adr);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/portfolio/${ticker}/my-position`, { params: activeId ? { portfolio_id: activeId } : {}, withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker, activeId]);

  // Live overlay: recompute MV / unrealized / daily P/L every tick.
  // Owners only — for watchlist-only viewers, `data.is_owned === false`
  // and the position-related fields don't exist on the payload.
  const live = useMemo(() => {
    if (!data || !data.is_owned) return null;
    const lp = livePrices?.[data.ticker.toUpperCase()]?.price;
    const livePx = typeof lp === 'number' && lp > 0 ? lp : data.price;
    const shares = Number(data.shares) || 0;
    const prevClose = Number(data.previous_close) || 0;
    const totalCost = Number(data.total_cost) || 0;
    const marketValue = shares * livePx;
    const unrealizedPl = marketValue - totalCost;
    const unrealizedPct = totalCost > 0 ? (unrealizedPl / totalCost) * 100 : 0;
    const dailyPl = (livePx > 0 && prevClose > 0) ? shares * (livePx - prevClose) : 0;
    const dailyPlPct = prevClose > 0 ? ((livePx - prevClose) / prevClose) * 100 : 0;
    return { livePx, marketValue, unrealizedPl, unrealizedPct, dailyPl, dailyPlPct };
  }, [data, livePrices]);

  if (loading) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground" data-testid="my-position-loading">
        Loading your position…
      </div>
    );
  }
  if (!data || !data.is_owned) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground" data-testid="my-position-empty">
        No position for this ticker.
      </div>
    );
  }

  // Sector / vehicle pill — REIT and MLP take priority over generic sector
  const vehicleLabel = data.is_reit ? 'REIT' : data.is_mlp ? 'MLP' : data.is_etf ? 'ETF' : null;
  const sectorLabel = data.sector && data.sector.length > 0 ? data.sector : null;

  return (
    <div className="space-y-4" data-testid="my-position-tab">
      {/* Headline strip — Live, Day P/L, OHLC */}
      <div className="rounded-lg border bg-card p-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold tracking-wide">Live Position</h3>
            {vehicleLabel && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold border border-violet-300/60 dark:border-violet-800/60 bg-violet-50/60 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300">
                {vehicleLabel}
              </span>
            )}
            {sectorLabel && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold border border-border bg-muted/40 text-muted-foreground" data-testid="my-position-sector">
                {sectorLabel}
              </span>
            )}
          </div>
          <div className={`flex items-center gap-1 text-base font-bold font-mono ${tone(live.dailyPl)}`} data-testid="my-position-day-pl">
            {live.dailyPl > 0 ? <TrendingUp className="w-4 h-4" /> : live.dailyPl < 0 ? <TrendingDown className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
            <span>{fmtMoney(live.dailyPl, { sign: true })}</span>
            <span className="text-xs opacity-80">({fmtPct(live.dailyPlPct)})</span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat
            label="Live"
            value={fmtMoney(live.livePx)}
            valueTone={tone(live.livePx - (data.previous_close || live.livePx))}
            testId="my-position-live-px"
            hint="Most recent traded price — ticks via websocket"
          />
          <Stat
            label="Prev Close"
            value={fmtMoney(data.previous_close)}
            testId="my-position-prev-close"
            hint="Yesterday's closing price — baseline for Day P/L"
          />
          <Stat
            label="Open"
            value={fmtMoney(data.daily_open)}
            testId="my-position-open"
            hint="Today's regular-session opening print (the first trade at 9:30 AM ET). Often near — but not always equal to — the previous close."
          />
          <Stat
            label="Close"
            value={fmtMoney(data.daily_close)}
            testId="my-position-close"
            hint="Today's regular-session close (4:00 PM ET). After the bell, falls back to the latest after-hours print as the de-facto close (mirrors Robinhood's behaviour). Renders ' — ' only when neither value has printed yet."
          />
          <Stat
            label="Day High"
            value={fmtMoney(data.daily_high)}
            testId="my-position-high"
            hint="Highest intraday print so far today."
          />
          <Stat
            label="Day Low"
            value={fmtMoney(data.daily_low)}
            testId="my-position-low"
            hint="Lowest intraday print so far today."
          />
          <Stat
            label="Pre-Market"
            value={fmtMoney(data.pre_market)}
            testId="my-position-premarket"
            caption={data.pre_market != null && data.daily_date && data.daily_date !== todayInET() ? formatPriorSessionCaption(data.daily_date) : null}
            hint={
              data.daily_date
                ? `Last trade in the pre-market window (4:00–9:30 AM ET) of ${data.daily_date} — the most recent finalised session. Today's pre-market doesn't publish here until after the 4 PM ET close, when Massive seals the daily bar.`
                : "Last trade in the pre-market window (4:00–9:30 AM ET). Today's pre-market doesn't publish here until after the 4 PM ET close, when the daily bar is finalised."
            }
          />
          <Stat
            label="After-Hours"
            value={fmtMoney(data.after_hours)}
            testId="my-position-afterhours"
            caption={data.after_hours != null && data.daily_date && data.daily_date !== todayInET() ? formatPriorSessionCaption(data.daily_date) : null}
            hint={
              data.daily_date
                ? `Last trade in the after-hours window (4:00–8:00 PM ET) of ${data.daily_date} — the most recent finalised session. Today's after-hours fills here after tonight's 8 PM ET close.`
                : "Last trade in the after-hours window (4:00–8:00 PM ET). Today's after-hours fills here after the 8 PM ET close, once the daily bar is finalised."
            }
          />
          <Stat
            label="Volume"
            value={
              <span className="inline-flex items-center gap-1.5">
                <span>{fmtVolume(data.daily_volume)}</span>
                {(() => {
                  const badge = unusualVolumeBadge(data.daily_volume, data.avg_volume);
                  return badge ? (
                    <span
                      className={`text-[9px] font-bold px-1 py-[1px] rounded-sm whitespace-nowrap ${badge.classes}`}
                      data-testid="my-position-volume-badge"
                      title={
                        badge.severity === 'heavy'
                          ? 'Heavy activity — today\'s volume is well above the 3-month average. Often a news / earnings / dividend-announcement day. Worth a quick check of the Insights tab.'
                          : badge.severity === 'above'
                            ? 'Above-average activity vs the 3-month average.'
                            : 'Light activity vs the 3-month average — a quiet day.'
                      }
                    >
                      {badge.label}
                    </span>
                  ) : null;
                })()}
              </span>
            }
            testId="my-position-volume"
            caption={data.daily_volume != null && data.volume_source === 'bar' && data.daily_date && data.daily_date !== todayInET() ? formatPriorSessionCaption(data.daily_date) : null}
            hint={
              data.volume_source === 'live'
                ? "Today's intraday traded volume (live — ticks up as the session progresses). The pill compares against the 3-month average."
                : data.daily_date
                  ? `Total volume traded on ${data.daily_date} — the most recent finalised session. Today's running total will fill in once the snapshot pipeline ingests an intraday print.`
                  : "Volume not yet available."
            }
          />
          <Stat
            label="Avg Volume"
            value={fmtVolume(data.avg_volume)}
            testId="my-position-avg-volume"
            hint="Average daily trading volume over the trailing 3 months. Compare today's Volume against this to gauge whether activity is unusual."
          />
        </div>
      </div>

      {/* Position aggregates — Avg cost, Total Paid, MV, Unrealized */}
      <div className="rounded-lg border bg-card p-3">
        <h3 className="text-sm font-bold tracking-wide mb-2">Your Position</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat
            label="Shares"
            value={fmtShares(data.shares)}
            testId="my-position-shares"
            hint={`${data.lot_count} ${data.lot_count === 1 ? 'lot' : 'lots'}`}
          />
          <Stat
            label="Avg Cost"
            value={fmtMoney(data.avg_cost)}
            testId="my-position-avg-cost"
            hint="Weighted-average cost per share across every lot"
          />
          <Stat
            label="Total Paid"
            value={fmtMoney(data.total_cost)}
            testId="my-position-total-paid"
            hint="Total out-of-pocket: shares × avg cost"
          />
          <Stat
            label="Market Value"
            value={fmtMoney(live.marketValue)}
            valueTone={tone(live.marketValue - data.total_cost)}
            testId="my-position-market-value"
            hint="Live: shares × current price"
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
          <Stat
            label="Unrealized P/L"
            value={fmtMoney(live.unrealizedPl, { sign: true })}
            valueTone={tone(live.unrealizedPl)}
            testId="my-position-unrealized-dollar"
            hint="Market Value − Total Paid"
          />
          <Stat
            label="Unrealized %"
            value={fmtPct(live.unrealizedPct)}
            valueTone={tone(live.unrealizedPct)}
            testId="my-position-unrealized-pct"
          />
          <Stat
            label="First Buy"
            value={data.first_buy_date ? new Date(data.first_buy_date).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—'}
            testId="my-position-first-buy"
          />
          <Stat
            label="Day P/L"
            value={fmtMoney(live.dailyPl, { sign: true })}
            valueTone={tone(live.dailyPl)}
            testId="my-position-daily-pl"
          />
        </div>
      </div>

      {/* Dividend stats specific to THIS position */}
      <div className="rounded-lg border bg-card p-3">
        <h3 className="text-sm font-bold tracking-wide mb-2">Dividend Stats</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat
            label={<AbbrTip term="YoC">YoC</AbbrTip>}
            value={data.yoc_pct !== null && data.yoc_pct !== undefined ? `${data.yoc_pct.toFixed(2)}%` : '—'}
            valueTone={tone(data.yoc_pct)}
            testId="my-position-yoc"
            hint="Yield on Cost: annual dividend ÷ your avg cost. Locked in at purchase."
          />
          <Stat
            label="Annual Income"
            value={fmtMoney(data.annual_income_from_position)}
            valueTone="text-emerald-600 dark:text-emerald-400"
            testId="my-position-annual-income"
            hint={`Based on $${(data.annual_dividend || 0).toFixed(2)}/share × ${fmtShares(data.shares)} shares`}
          />
          <Stat
            label="% of Portfolio Income"
            value={`${(data.pct_of_portfolio_income || 0).toFixed(1)}%`}
            testId="my-position-income-share"
            hint={`${fmtMoney(data.annual_income_from_position)} of ${fmtMoney(data.portfolio_total_annual_income)} projected annual dividends ${data.income_scope === 'portfolio' ? `in ${data.income_scope_name || 'this portfolio'}` : 'across all your portfolios'} — latest declared payout × payments per year for every holding, same math as the income-concentration banner`}
            caption={data.income_scope === 'portfolio' ? (data.income_scope_name || 'this portfolio') : 'all portfolios'}
          />
          <Stat
            label="Dividend Frequency"
            value={data.frequency || '—'}
            testId="my-position-frequency"
          />
        </div>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {/* Dividend growth since first buy — only if we can compute it.
              When dividend history is missing or first buy was within
              the last ~6 months, we display a friendly "—" instead. */}
          <div
            className={`rounded-md border bg-card/60 px-2.5 py-1.5 ${data.div_growth_since_first_buy_pct !== null && data.div_growth_since_first_buy_pct !== undefined ? '' : 'opacity-70'}`}
            data-testid="my-position-div-growth-since-buy"
          >
            <p className="text-[9px] uppercase tracking-wide text-muted-foreground font-bold">
              Dividend growth since your first buy
            </p>
            {data.div_growth_since_first_buy_pct !== null && data.div_growth_since_first_buy_pct !== undefined ? (
              <p className={`text-sm font-bold font-mono ${tone(data.div_growth_since_first_buy_pct)}`}>
                {fmtPct(data.div_growth_since_first_buy_pct)}
                {data.div_rate_at_first_buy && (
                  <span className="ml-1.5 text-[10px] text-muted-foreground font-normal">
                    (${data.div_rate_at_first_buy.toFixed(2)} → ${(data.annual_dividend || 0).toFixed(2)} / yr)
                  </span>
                )}
              </p>
            ) : (
              <p className="text-sm font-bold font-mono text-muted-foreground">
                — <span className="text-[10px] font-normal">need full dividend year since first buy</span>
              </p>
            )}
          </div>
          <div className="rounded-md border bg-card/60 px-2.5 py-1.5" data-testid="my-position-current-yield">
            <p className="text-[9px] uppercase tracking-wide text-muted-foreground font-bold">
              Current Yield (Market)
            </p>
            <p className="text-sm font-bold font-mono">
              {data.dividend_yield ? `${Number(data.dividend_yield).toFixed(2)}%` : '—'}
              <span className="ml-1.5 text-[10px] text-muted-foreground font-normal">
                vs your YoC of {data.yoc_pct !== null ? `${data.yoc_pct.toFixed(2)}%` : '—'}
              </span>
            </p>
          </div>
        </div>
        <PositionXirrTile ticker={ticker} />

        {/* Round 95t (Jun 2026) — Per-position ADR withholding row.
            Only renders when the modal stock is an ADR with a known
            country WHT rate > 0 AND the user holds shares with an
            annual dividend amount. Sits inside Dividend Stats so the
            user sees "Annual Income $XYZ" right next to "Foreign
            withholding −$N · ~$N recoverable as FTC". */}
        {(() => {
          if (!stock?.is_adr || !adr?.is_adr || !adr.country) return null;
          const rate = adr.withholding_rate;
          if (typeof rate !== 'number' || rate <= 0) return null;
          const source = typeof adr.withholding_source_rate === 'number' ? adr.withholding_source_rate : rate;
          const cc = (adr.country_code || stock?.country_code || '').toUpperCase();
          const gross = Number(data.annual_income_from_position || 0);
          if (gross <= 0) return null;
          const wht = gross * (rate / 100);
          const net = gross - wht;
          const excessAmt = adr.withholding_reclaim && source > rate ? gross * ((source - rate) / 100) : 0;
          return (
            <div
              className="mt-2 rounded-md border border-amber-300/60 dark:border-amber-700/40 bg-amber-50/50 dark:bg-amber-950/25 px-3 py-2"
              data-testid="my-position-adr-wht"
            >
              <div className="flex items-center gap-2 mb-1 text-[11px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-200">
                <Globe className="w-3.5 h-3.5" />
                <span>Foreign <AbbrTip term="WHT">Withholding</AbbrTip> on This Position</span>
                <span className="ml-auto font-normal normal-case text-amber-700/80 dark:text-amber-300/80" data-testid="my-position-adr-wht-rate">
                  {cc ? `${ccToFlag(cc)} ` : ''}{adr.country} · {rate}% <AbbrTip term="TREATY_RATE">treaty rate</AbbrTip>{source > rate ? ` · ${source}% at source` : ''}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <div className="text-[9px] font-bold text-muted-foreground uppercase tracking-wide">Gross / yr</div>
                  <div className="font-mono font-bold">${gross.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
                <div>
                  <div className="text-[9px] font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wide"><AbbrTip term="WHT">Withheld</AbbrTip></div>
                  <div className="font-mono font-bold text-amber-700 dark:text-amber-300">−${wht.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
                <div>
                  <div className="text-[9px] font-bold text-muted-foreground uppercase tracking-wide">You receive</div>
                  <div className="font-mono font-bold">${net.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
              </div>
              <p className="mt-1.5 text-[10px] text-muted-foreground leading-snug">
                Up to ${wht.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} qualifies for the IRS{' '}
                <AbbrTip term="FTC">Foreign Tax Credit</AbbrTip>{' '}
                on Form 1116 (taxable accounts only). Treaty rates assume a US-resident with a W-8BEN on file via DTC. Not tax advice.
              </p>
              {excessAmt > 0 && (
                <p className="mt-1 text-[10px] text-amber-800 dark:text-amber-200 leading-snug" data-testid="my-position-adr-wht-reclaim">
                  {adr.country} actually takes {source}% at source — about ${excessAmt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/yr above the treaty rate is reclaimable. {adr.withholding_note}
                </p>
              )}
              {adr.ira_note && (
                <p className="mt-1 text-[10px] text-muted-foreground leading-snug" data-testid="my-position-adr-ira-note">{adr.ira_note}</p>
              )}

              {/* Round 95u (Jun 2026) — "Compare across accounts" pill row.
                  Only shown when the user has lots split across 2+
                  distinct (portfolio, account_type) buckets — otherwise
                  the per-position math above already says everything. */}
              {(() => {
                const buckets = Array.isArray(data.account_breakdown) ? data.account_breakdown : [];
                if (buckets.length < 2) return null;
                const pricePerShare = Number(data.annual_dividend || 0);
                if (pricePerShare <= 0) return null;
                return (
                  <div
                    className="mt-2 pt-2 border-t border-amber-200/60 dark:border-amber-700/40"
                    data-testid="my-position-wht-account-compare"
                  >
                    <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1.5">
                      Compare across your accounts
                    </p>
                    <div className="space-y-1">
                      {buckets.map((b, i) => {
                        const grossB = b.shares * pricePerShare;
                        const whtB = grossB * (rate / 100);
                        const at = b.account_type;
                        const isTaxable = at === 'taxable';
                        const isRetirement = ['roth_ira','traditional_ira','ira','hsa','sep_ira','simple_ira','401k'].includes(at);
                        const isUntagged = !at;
                        let label;
                        let ftcCopy;
                        let ftcTone = 'text-amber-700 dark:text-amber-300';
                        if (isTaxable) {
                          label = 'Taxable';
                          ftcCopy = <>${whtB.toFixed(2)} FTC recovered ✓</>;
                          ftcTone = 'text-emerald-700 dark:text-emerald-400';
                        } else if (isRetirement) {
                          label = at === 'roth_ira' ? 'Roth IRA' : at.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase());
                          ftcCopy = <>$0 FTC (forfeited inside tax-advantaged account)</>;
                          ftcTone = 'text-rose-700 dark:text-rose-400';
                        } else if (isUntagged) {
                          label = 'Untagged';
                          ftcCopy = (
                            <>
                              up to ${whtB.toFixed(2)} —{' '}
                              <button
                                type="button"
                                onClick={() => {
                                  // Round 95v (Jun 2026) — jump straight
                                  // to the My Portfolio tab where the
                                  // user can click the new account-type
                                  // pill on each lot. Avoids a second
                                  // modal layer just to tag.
                                  window.dispatchEvent(new CustomEvent('stock-modal-switch-tab', { detail: { tab: 'my-portfolio' } }));
                                }}
                                className="underline font-semibold hover:text-foreground"
                                data-testid="tag-lots-link"
                              >
                                tag your lots →
                              </button>
                            </>
                          );
                        } else {
                          label = at;
                          ftcCopy = <>${whtB.toFixed(2)} (account treatment unclear)</>;
                        }
                        const pf = b.portfolio_name ? `${b.portfolio_name} · ` : '';
                        return (
                          <div key={i} className="text-[11px] leading-snug flex items-baseline gap-2">
                            <span className="font-mono w-12 text-right text-amber-900 dark:text-amber-100 font-bold">
                              {b.shares.toLocaleString('en-US', { maximumFractionDigits: 4 })}
                            </span>
                            <span className="text-amber-900 dark:text-amber-100 min-w-0 flex-1">
                              {pf}<strong>{label}</strong> · <span className={ftcTone}>{ftcCopy}</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
